import {
  Box,
  Button,
  Container,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { InstitucionalIcon } from "../../../common/assets/icons/Institucional";
import { UserIcon } from "../../../common/assets/icons/User";
import { useAppContext } from "../../../common/context/AppContext";
import { UserService } from "../../../common/services/UserService";
import { UserType, UserTypeSelect } from "../../../common/types";
import { HookedField } from "../../components/HookedField";
import { HookedForm } from "../../components/HookedForm";
import { OptionField } from "../../components/OptionField";
import countries from "../../../common/assets/countries.json";
import { RoutePaths } from "../../consts/enums";

const schema = yup
  .object({
    website_url: yup
      .string()
      .required()
      .nullable()
      .max(600)
      .trim()
      .label("Location"),
    company_name: yup
      .string()
      .required()
      .nullable()
      .max(600)
      .trim()
      .label("Company Name"),
    location: yup
      .string()
      .required()
      .nullable()
      .max(600)
      .trim()
      .label("Website"),
  })
  .required();

const TypeOfBuyer = [
  {
    Icon: UserIcon,
    label: "Individual",
    value: UserTypeSelect.Individual,
    description: "Acquisition Entrepreneur or Angel Investor",
  },
  {
    Icon: InstitucionalIcon,
    label: "Institutional",
    value: UserTypeSelect.INSTITUTIONAL,
    description: "Fund or Corporate Development",
  },
];

export const Questionnarie = () => {
  const { user, updateUser } = useAppContext();
  const formHook = useForm({
    defaultValues: {
      ...user,
      website_url: user.website_url ? user.website_url : "https://",
    },
    resolver: user.active_role === "seller" ? yupResolver(schema) : undefined,
  });
  const navigate = useNavigate();
  const [buyerType, setBuyerType] = useState<UserTypeSelect | undefined>(
    user.active_role === "seller"
      ? UserTypeSelect.INSTITUTIONAL
      : UserTypeSelect.Individual
  ); // eslint-disable-line @typescript-eslint/no-unused-vars

  const getBuyerType = (v: UserTypeSelect) => {
    setBuyerType(v);
  };

  const handleContinue = async (values: any) => {
    if (buyerType === UserTypeSelect.INSTITUTIONAL || user.sellerFull) {
      if (values.website_url) {
        if (!values.website_url.includes("https://")) {
          values.website_url = "https://" + values.website_url;
        }
      }
      const res = await UserService.OnboardStepPatch(
        {
          ...values,
          buyer_genre: buyerType,
        },
        "step2/"
      );
      updateUser((prev) => ({
        ...prev,
        ...res,
        buyer_genre: buyerType,
      }));
    }
    if (buyerType === UserTypeSelect.Individual) {
      const res = await UserService.OnboardStepPatch(
        {
          buyer_genre: buyerType,
        },
        "step2/"
      );
      updateUser((prev) => ({
        ...prev,
        ...res,
        buyer_genre: buyerType,
      }));
    }
    navigate(RoutePaths.REGISTER + "/questionnaire");
  };

  useEffect(() => {
    if (!user.active_role) navigate(RoutePaths.REGISTER + "/basic");
  }, []);

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <HookedForm formHook={formHook}>
        <Container
          maxWidth="sm"
          sx={{
            paddingTop: 15,
            // padding: 5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {user.active_role === UserType.Buyer && (
            <OptionField
              name="typeOfBuyer"
              options={TypeOfBuyer}
              question="What type of buyer are you?"
              onChange={(v) => getBuyerType(v as UserTypeSelect)}
            />
          )}
          {(user.active_role === UserType.Seller ||
            buyerType === UserTypeSelect.INSTITUTIONAL) && (
            <>
              <Box sx={{ marginBottom: 2, marginTop: 2 }}>
                <Typography variant="h3" sx={{ marginBottom: 1 }}>
                  Your Location
                </Typography>
                <Typography variant="body2" sx={{ color: "common.gray" }}>
                  Please provide where are you located
                </Typography>
              </Box>
              <HookedField
                select
                defaultValue="GB"
                component={TextField}
                fullWidth
                label="Where are you located?"
                name="location"
                variant="filled"
              >
                {countries.map((x) => (
                  <MenuItem key={x.code} value={x.code}>
                    <ListItemText sx={{ display: "inline-flex", margin: 0 }}>
                      {x.name}
                    </ListItemText>
                  </MenuItem>
                ))}
              </HookedField>
              <Box sx={{ marginBottom: 2, marginTop: 2 }}>
                <Typography variant="h3">Company Name</Typography>
              </Box>
              <HookedField
                component={TextField}
                fullWidth
                label="Company Name"
                name="company_name"
                variant="filled"
              />
              <Box sx={{ marginBottom: 2, marginTop: 2 }}>
                <Typography variant="h3" sx={{ marginBottom: 1 }}>
                  Website
                </Typography>
                <Typography variant="body2" sx={{ color: "common.gray" }}>
                  Please provide your company&apos;s website
                </Typography>
              </Box>
              <HookedField
                component={TextField}
                fullWidth
                label="Website"
                name="website_url"
                variant="filled"
                helperText="https://yourwebsite.com or http://www.yourwebsite.com"
              />
            </>
          )}

          <Box sx={{ width: "100%", marginTop: 2 }}>
            <Button
              variant="roundgradient"
              sx={{ backgroundColor: "common.lightgray" }}
              onClick={formHook.handleSubmit(handleContinue)}
            >
              Continue
            </Button>
          </Box>
        </Container>
      </HookedForm>
    </Box>
  );
};
